import { memo } from "react";
import styled, { createGlobalStyle } from "styled-components";
import CommonModal from "../../../../../Component/Ui-Kits/Modal/Common/CommonModal";
import DetailsInfo from "../../../../../CommonElements/DetailsInfo";
import { IConveyanceInfoModalProps } from "../../types";

const ModalGlobalStyle = createGlobalStyle`
  .conveyance-info-modal .modal-title {
    font-size: 15px;
    font-weight: 600;
  }

  .conveyance-info-modal .modal-content {
    overflow: hidden;
  }

  .conveyance-info-modal .modal-body {
    overflow: hidden;
  }

  @media (max-width: 575px) {
    .conveyance-info-modal {
      width: 100%;
      max-width: 100%;
      height: 100dvh;
      margin: 0;
    }

    .conveyance-info-modal .modal-content {
      height: 100dvh;
      max-height: 100dvh;
      border: 0;
      border-radius: 0;
    }

    .conveyance-info-modal .modal-header {
      flex: 0 0 auto;
    }

    .conveyance-info-modal .modal-body {
      display: flex;
      min-height: 0;
      flex: 1 1 auto;
    }
  }
`;

const ModalContent = styled.div`
  display: flex;
  height: min(78dvh, 640px);
  max-height: calc(100dvh - 32px);
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #f8fafc;
  -webkit-overflow-scrolling: touch;

  @supports not (height: 1dvh) {
    height: min(78vh, 640px);
    max-height: calc(100vh - 32px);
  }

  @media (max-width: 575px) {
    height: 100%;
    max-height: none;
    flex: 1 1 auto;
  }
`;

const SummaryHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`;

const MemberBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const Avatar = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 12px;
  color: #075985;
  background: #e0f2fe;
  box-shadow: inset 0 0 0 1px #bae6fd;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
`;

const MemberName = styled.p`
  margin: 0;
  color: #0f172a;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
`;

const MemberMeta = styled.p`
  margin: 3px 0 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MetaPills = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;

  @media (max-width: 575px) {
    justify-content: flex-start;
  }
`;

const Pill = styled.span<{ $tone?: "success" | "danger" | "neutral" }>`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 4px 9px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === "success"
        ? "#bbf7d0"
        : $tone === "danger"
        ? "#fecdd3"
        : "#e2e8f0"};
  border-radius: 999px;
  color: ${({ $tone }) =>
    $tone === "success"
      ? "#15803d"
      : $tone === "danger"
      ? "#be123c"
      : "#475569"};
  background: ${({ $tone }) =>
    $tone === "success"
      ? "#f0fdf4"
      : $tone === "danger"
      ? "#fff1f2"
      : "#fff"};
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
`;

const HighlightSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 12px;
  padding: 14px 18px 0;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const RouteCard = styled.div`
  min-width: 0;
  padding: 14px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
`;

const SectionLabel = styled.p`
  margin: 0 0 8px 0;
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const RouteLine = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
`;

const RoutePoint = styled.div`
  min-width: 0;
`;

const RouteText = styled.p`
  margin: 0;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RouteCaption = styled.p`
  margin: 3px 0 0 0;
  color: #64748b;
  font-size: 11px;
`;

const RouteConnector = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  color: #2563eb;
  background: #eff6ff;
  font-size: 12px;
  font-weight: 700;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  min-width: 0;
  padding: 11px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
`;

const StatCardLabel = styled.p`
  margin: 0 0 5px 0;
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const StatCardValue = styled.p`
  margin: 0;
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LastApprovalSection = styled.div`
  padding: 10px 18px 0;

  @media (max-width: 575px) {
    padding: 10px 12px 0;
  }
`;

const ApprovalHistoryGrid = styled.div`
  display: grid;
  gap: 6px;
`;

const LastApprovalCard = styled.div`
  padding: 8px 10px;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
  background: #ffffff;
`;

const ApprovalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ApprovalBody = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 0.8fr) minmax(0, 1.4fr);
  gap: 8px;
  align-items: center;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const ApprovalPerson = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;

const ApprovalAvatar = styled(Avatar)`
  width: 30px;
  height: 30px;
  flex-basis: 30px;
  border-radius: 8px;
  color: #4338ca;
  background: #eef2ff;
  box-shadow: inset 0 0 0 1px #c7d2fe;
  font-size: 11px;
`;

const ApprovalName = styled.p`
  margin: 0;
  color: #0f172a;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ApprovalMeta = styled.p`
  margin: 2px 0 0 0;
  color: #64748b;
  font-size: 10px;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const ApprovalInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`;

const ApprovalInfo = styled.div`
  min-width: 0;
`;

const ApprovalInfoLabel = styled.p`
  margin: 0 0 2px 0;
  color: #64748b;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const ApprovalInfoValue = styled.p`
  margin: 0;
  color: #0f172a;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DetailsScroll = styled.div`
  flex: 0 0 auto;
  min-height: 0;
  overflow: visible;
  padding: 14px 18px 16px;

  @media (max-width: 575px) {
    padding: 12px;
  }

  .modal-toggle-wrapper,
  .modal-toggle-wrapper > div {
    min-width: 0;
  }

  ul {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin: 0 !important;
    padding: 0 !important;

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
    }
  }

  li {
    min-width: 0;
    padding: 12px 13px;
    border: 1px solid #eef2f7;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.03);
    list-style: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease,
      transform 0.15s ease;
  }

  li:hover {
    border-color: #dbeafe;
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
  }

  li .row {
    align-items: center;
    margin: 0;
  }

  li [class*="col-"] {
    padding: 0;
  }

  li .col-md-1 {
    display: none;
  }

  li .col-md-4,
  li .col-md-7 {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
  }

  li .col-md-4 p {
    margin: 0 0 4px 0 !important;
    color: #64748b;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase !important;
  }

  li .col-md-7 p {
    margin: 0 !important;
    color: #0f172a;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    overflow-wrap: anywhere;
    text-transform: none !important;
  }

  li .col-md-7 i,
  li .col-md-7 svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    background: #eff6ff;
  }

  iframe {
    max-height: 360px;
  }
`;

const getStatusTone = (status?: string) => {
  if (status === "approved") return "success";
  if (status === "rejected") return "danger";
  return "neutral";
};

const getInitials = (name?: string) =>
  name
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("") || "NA";

const formatAmount = (value?: string | number | null) =>
  Number(value ?? 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

const ConveyanceInfoModal = ({
  isOpen,
  onClose,
  conveyanceInfoDetails,
}: IConveyanceInfoModalProps) => {
  const approvalHistory = conveyanceInfoDetails?.lastApprovals ?? [];

  return (
    <>
    <ModalGlobalStyle />
    <CommonModal
      headerClassName="bg-primary"
      titleClassName="text-white"
      title="Conveyance Details"
      size="lg"
      modalClassName="conveyance-info-modal"
      modalBodyClassName="p-0"
      centered
      toggle={onClose}
      isOpen={isOpen}
      showFooter={false}
      contentClassName="overflow-hidden"
    >
      <ModalContent>
        {conveyanceInfoDetails ? (
          <>
            <SummaryHeader>
              <MemberBlock>
                <Avatar>
                  {getInitials(conveyanceInfoDetails.teamMember.name)}
                </Avatar>
                <div>
                  <MemberName>
                    {conveyanceInfoDetails.teamMember.name}
                  </MemberName>
                  <MemberMeta title={conveyanceInfoDetails.teamMember.email}>
                    {conveyanceInfoDetails.teamMember.email}
                  </MemberMeta>
                </div>
              </MemberBlock>
              <MetaPills>
                <Pill>{conveyanceInfoDetails.id}</Pill>
                <Pill>{conveyanceInfoDetails.date}</Pill>
                <Pill $tone={getStatusTone(conveyanceInfoDetails.status)}>
                  {conveyanceInfoDetails.status}
                </Pill>
              </MetaPills>
            </SummaryHeader>

            <HighlightSection>
              <RouteCard>
                <SectionLabel>Travel Route</SectionLabel>
                <RouteLine>
                  <RoutePoint>
                    <RouteText title={conveyanceInfoDetails.travelFrom}>
                      {conveyanceInfoDetails.travelFrom || "NA"}
                    </RouteText>
                    <RouteCaption>From</RouteCaption>
                  </RoutePoint>
                  <RouteConnector>to</RouteConnector>
                  <RoutePoint>
                    <RouteText title={conveyanceInfoDetails.travelTo}>
                      {conveyanceInfoDetails.travelTo || "NA"}
                    </RouteText>
                    <RouteCaption>To</RouteCaption>
                  </RoutePoint>
                </RouteLine>
              </RouteCard>

              <StatGrid>
                <StatCard>
                  <StatCardLabel>Claimed</StatCardLabel>
                  <StatCardValue>
                    {formatAmount(conveyanceInfoDetails.amount)}
                  </StatCardValue>
                </StatCard>
                <StatCard>
                  <StatCardLabel>Approved</StatCardLabel>
                  <StatCardValue>
                    {formatAmount(conveyanceInfoDetails.approvedAmount)}
                  </StatCardValue>
                </StatCard>
                <StatCard>
                  <StatCardLabel>Distance</StatCardLabel>
                  <StatCardValue>
                    {conveyanceInfoDetails.distance || "NA"}
                  </StatCardValue>
                </StatCard>
              </StatGrid>
            </HighlightSection>

            {approvalHistory.length > 0 ? (
              <LastApprovalSection>
                <ApprovalHeader>
                  <SectionLabel>Last Approvals</SectionLabel>
                  <Pill>{approvalHistory.length}</Pill>
                </ApprovalHeader>
                <ApprovalHistoryGrid>
                  {approvalHistory.map((approval, index) => {
                    const approverName =
                      approval.approverInfo.name || approval.by || "NA";

                    return (
                      <LastApprovalCard key={`${approval.date}-${index}`}>
                        <ApprovalBody>
                          <ApprovalPerson>
                            <ApprovalAvatar>
                              {getInitials(approverName)}
                            </ApprovalAvatar>
                            <div>
                              <ApprovalName>{approverName}</ApprovalName>
                              <ApprovalMeta>
                                {approval.approverInfo.designation || "NA"}
                              </ApprovalMeta>
                            </div>
                          </ApprovalPerson>
                          <ApprovalInfoGrid>
                            <ApprovalInfo>
                              <ApprovalInfoLabel>Email</ApprovalInfoLabel>
                              <ApprovalInfoValue>
                                {approval.approverInfo.email || "NA"}
                              </ApprovalInfoValue>
                            </ApprovalInfo>
                            <ApprovalInfo>
                              <ApprovalInfoLabel>Date Time</ApprovalInfoLabel>
                              <ApprovalInfoValue>
                                {approval.date || "NA"}
                              </ApprovalInfoValue>
                            </ApprovalInfo>
                            <ApprovalInfo>
                              <ApprovalInfoLabel>Designation</ApprovalInfoLabel>
                              <ApprovalInfoValue>
                                {approval.approverInfo.designation || "NA"}
                              </ApprovalInfoValue>
                            </ApprovalInfo>
                          </ApprovalInfoGrid>
                        </ApprovalBody>
                      </LastApprovalCard>
                    );
                  })}
                </ApprovalHistoryGrid>
              </LastApprovalSection>
            ) : null}

            <DetailsScroll>
              <div className="modal-toggle-wrapper">
                <DetailsInfo
                  detailsInfo={conveyanceInfoDetails}
                  exclusionKeys={[
                    "teamMember",
                    "id",
                    "date",
                    "status",
                    "travelFrom",
                    "travelTo",
                    "amount",
                    "approvedAmount",
                    "distance",
                    "lastApproval",
                    "lastApprovals",
                  ]}
                />
              </div>
            </DetailsScroll>
          </>
        ) : null}
      </ModalContent>
    </CommonModal>
    </>
  );
};

export default memo(ConveyanceInfoModal);
